import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { z } from 'zod';
import {
  Edit2, Trash2, Mail, Plus,
  Lock, Send, Clock, Users, Shield, UserCog, Briefcase, Power,
  MoreVertical, RefreshCw, X
} from 'lucide-react';

// Services
import { bookingService } from '@/services/api/booking.service';
import { teamService } from '@/services/api/team.service';

// Hooks
import {
  useInvitations,
  useCreateInvitation,
  useResendInvitation,
  useRevokeInvitation,
  useDeleteInvitation
} from '@/hooks/use-invitations';
import {
  useToggleUserActive,
  useChangeUserRole,
  useDeleteUser
} from '@/hooks/use-users';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/contexts/subscription-context';

// Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { FloatingActionButton } from '@/components/ui/floating-action-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { UpgradeDialog } from '@/components/subscription/upgrade-dialog';
import { UpgradeNudge } from '@/components/subscription/UpgradeNudge';
import { LimitUsageIndicator } from '@/components/subscription/LimitUsageIndicator';
import { EditMemberDialog } from '@/components/team/EditMemberDialog';
import { isSubscriptionError, extractSubscriptionError } from '@/lib/subscription-error-handler';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { cn } from '@/lib/utils';

// Types
import type { User } from '@/types/auth';
import type { Provider } from '@/types/booking';
import type { TeamMember } from '@/types/team';
import type { UserInvitation } from '@/types/invitation';
import type { SubscriptionError } from '@/types/subscription';

// Adapt a TeamMember (from /v1/admin/team) into the existing User shape used by edit/remove handlers.
function memberToUser(member: TeamMember): User {
  return {
    id: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    phone: member.phone,
    role: member.role,
    active: member.active,
    createdAt: member.createdAt,
    updatedAt: member.createdAt,
    organizationId: 0,
  } as unknown as User;
}

// Adapt the inlined provider block on a TeamMember into the existing Provider shape used by EditMemberDialog.
function memberToProvider(member: TeamMember): Provider | null {
  if (!member.provider) return null;
  return {
    id: member.provider.id,
    publicId: member.provider.publicId,
    title: member.provider.title,
    specialization: member.provider.specialization,
    bio: member.provider.bio,
    active: member.provider.active,
    services: member.provider.services.map((s) => ({
      id: s.id,
      name: s.name,
      duration: s.durationMinutes,
    })),
    userId: member.id,
    organizationId: 0,
  } as unknown as Provider;
}

export default function TeamPage() {
  const { t } = useTranslation(['team', 'common', 'subscription', 'providers', 'invitations']);
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { canPerformAction, isFeatureEnabled, limits } = useSubscription();

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('members');
  const [invitationStatusFilter, setInvitationStatusFilter] = useState<string>('');

  // Dialog states
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditMemberDialogOpen, setIsEditMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [upgradeError, setUpgradeError] = useState<SubscriptionError | null>(null);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);

  useSetPageHeader(
    {
      title: t('team:title', 'Team Management'),
      sub: t('team:subtitle', 'Manage your team members and service providers'),
      actions: (
        <Button
          className="hidden md:inline-flex"
          size="md"
          onClick={() => {
            if (canPerformAction('users') && isFeatureEnabled('teamCollaboration')) {
              setIsInviteDialogOpen(true);
            } else {
              setShowUpgradeNudge(true);
            }
          }}
          iconLeft={<Send className="h-4 w-4" />}
        >
          <span>{t('team:inviteButton', 'Invite Team Member')}</span>
          {limits && (
            <span className="ml-1.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-medium leading-none tabular-nums">
              {limits.usage.users.current}/{limits.usage.users.limit}
            </span>
          )}
        </Button>
      ),
    },
    [t, canPerformAction, isFeatureEnabled, limits],
  );

  // ============ DATA QUERIES ============


  // Team members (single round-trip, includes provider+booking_counts)
  const { data: teamData, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['admin-team'],
    queryFn: () => teamService.list({ perPage: 100 }),
  });
  const teamMembers: TeamMember[] = useMemo(() => teamData?.members ?? [], [teamData]);

  // Services (for provider assignment in edit dialog)
  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  // Invitations
  const { data: invitations = [], isLoading: isLoadingInvitations } = useInvitations(
    activeTab === 'invitations' ? invitationStatusFilter : ''
  );

  // ============ MUTATIONS ============

  const createInvitation = useCreateInvitation();
  const resendInvitation = useResendInvitation();
  const revokeInvitation = useRevokeInvitation();
  const deleteInvitation = useDeleteInvitation();

  const toggleUserActive = useToggleUserActive();
  const changeUserRole = useChangeUserRole();
  const deleteUser = useDeleteUser();

  // ============ FORMS ============

  const inviteSchema = z.object({
    email: z.string().email(t('invitations:dialog.emailError', 'Invalid email address')),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    role: z.enum(['admin', 'staff']),
    personalMessage: z.string().max(500).optional(),
  });

  type InviteFormData = z.infer<typeof inviteSchema>;

  const inviteForm = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: 'staff',
      personalMessage: '',
    },
  });

  // ============ HANDLERS ============

  const handleInviteSubmit = async (data: InviteFormData) => {
    try {
      await createInvitation.mutateAsync({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        isProvider: true,
        personalMessage: data.personalMessage,
      });
      setIsInviteDialogOpen(false);
      inviteForm.reset();
      toast({
        title: t('invitations:toasts.sent', 'Invitation sent'),
        description: t('invitations:toasts.sentDescription', 'The invitation has been sent successfully'),
      });
    } catch (error: unknown) {
      if (isSubscriptionError(error)) {
        const subError = extractSubscriptionError(error as AxiosError);
        if (subError) setUpgradeError(subError);
      }
    }
  };

  const handleEditMember = (member: TeamMember) => {
    setSelectedMember(member);
    setIsEditMemberDialogOpen(true);
  };

  const handleNavigateToDetail = (member: TeamMember) => {
    setLocation(`/admin/team/${member.id}`);
  };

  const handleToggleMemberActive = async (member: TeamMember) => {
    if (member.id === currentUser?.id) return;
    await toggleUserActive.mutateAsync(member.id);
  };

  const handleChangeMemberRole = async (member: TeamMember, newRole: 'admin' | 'staff') => {
    if (member.id === currentUser?.id) return;
    if (member.role === newRole) return;
    await changeUserRole.mutateAsync({ id: member.id, role: newRole });
  };

  const handleDeleteMember = async (member: TeamMember) => {
    if (member.id === currentUser?.id) return;
    const name = `${member.firstName} ${member.lastName}`;
    if (confirm(t('invitations:confirmations.removeMember', { name }))) {
      await deleteUser.mutateAsync(member.id);
    }
  };

  const handleResendInvitation = async (id: number) => {
    await resendInvitation.mutateAsync(id);
  };

  const handleRevokeInvitation = async (id: number) => {
    if (confirm(t('invitations:confirmations.revoke'))) {
      await revokeInvitation.mutateAsync(id);
    }
  };

  const handleDeleteInvitation = async (id: number) => {
    if (confirm(t('invitations:confirmations.delete'))) {
      await deleteInvitation.mutateAsync(id);
    }
  };

  // ============ HELPERS ============

  const isSelf = (memberId: number) => memberId === currentUser?.id;

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'warn' | 'success' | 'neutral' | 'danger'> = {
      pending:  'warn',
      accepted: 'success',
      expired:  'neutral',
      revoked:  'danger',
    };
    return (
      <Badge tone={tones[status] ?? 'neutral'}>
        {t(`invitations:status.${status}`)}
      </Badge>
    );
  };

  const getTimeUntilExpiry = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs < 0) return t('invitations:card.expired');

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) return t('invitations:time.days', { count: diffDays, hours: diffHours });
    return t('invitations:time.hours', { count: diffHours });
  };

  // ============ MEMBER ROW: shared dropdown menu ============

  const MemberActionsMenu = ({ member }: { member: TeamMember }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
          aria-label={t('common:actions.more', 'Actions')}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => handleEditMember(member)}>
          <Edit2 className="w-4 h-4 mr-2" />
          {t('common:actions.edit', 'Edit')}
        </DropdownMenuItem>

        {!isSelf(member.id) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleChangeMemberRole(member, member.role === 'admin' ? 'staff' : 'admin')}
            >
              <Shield className="w-4 h-4 mr-2" />
              {member.role === 'admin'
                ? t('team:actions.makeStaff', 'Make Staff')
                : t('team:actions.makeAdmin', 'Make Admin')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleToggleMemberActive(member)}>
              <Power className="w-4 h-4 mr-2" />
              {member.active
                ? t('common:actions.deactivate', 'Deactivate')
                : t('common:actions.activate', 'Activate')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleDeleteMember(member)}
              className="text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('common:actions.remove', 'Remove')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // ============ RENDER ============

  return (
    <div className="space-y-6">
      {/* Feature Gate Alert */}
      {!isFeatureEnabled('teamCollaboration') && (
        <Alert className="bg-purple-50 border-purple-200">
          <Lock className="h-4 w-4 text-purple-600" />
          <AlertDescription className="ml-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-purple-900">
                {t('team:featureGate.message', 'Team collaboration features are not available on your current plan.')}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={() => {
                  setUpgradeError({
                    type: 'feature_disabled',
                    feature: 'team_collaboration',
                    featureName: 'Team Collaboration',
                    message: 'Team collaboration features are not available on your current plan',
                    currentPlan: limits?.currentPlan.name,
                    recommendedPlan: 'Pro',
                    description: 'Upgrade to unlock team management features.',
                  });
                }}
              >
                {t('team:featureGate.upgrade', 'Upgrade to Unlock')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent className="sm:max-w-[500px] max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('invitations:dialog.title', 'Invite Team Member')}</DialogTitle>
              <DialogDescription>
                {t('invitations:dialog.description', 'Send an invitation to join your team')}
              </DialogDescription>
            </DialogHeader>

            <Form {...inviteForm}>
              <form onSubmit={inviteForm.handleSubmit(handleInviteSubmit)} className="space-y-6">
                <FormField
                  control={inviteForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invitations:dialog.email', 'Email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t('invitations:dialog.emailPlaceholder', 'colleague@example.com')}
                          className="h-12"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={inviteForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invitations:dialog.firstName', 'First Name')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('invitations:dialog.firstNamePlaceholder', 'John')}
                            className="h-12"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={inviteForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invitations:dialog.lastName', 'Last Name')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('invitations:dialog.lastNamePlaceholder', 'Doe')}
                            className="h-12"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={inviteForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invitations:dialog.role', 'Role')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="staff">{t('invitations:dialog.roleStaff', 'Staff')}</SelectItem>
                          <SelectItem value="admin">{t('invitations:dialog.roleAdmin', 'Admin')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={inviteForm.control}
                  name="personalMessage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invitations:dialog.personalMessage', 'Personal Message (optional)')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('invitations:dialog.personalMessagePlaceholder', 'Add a personal note to the invitation...')}
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsInviteDialogOpen(false)}
                    className="h-11"
                  >
                    {t('common:actions.cancel', 'Cancel')}
                  </Button>
                  <Button type="submit" disabled={createInvitation.isPending} className="h-11">
                    {createInvitation.isPending
                      ? t('invitations:dialog.sending', 'Sending...')
                      : t('invitations:dialog.sendInvitation', 'Send Invitation')}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
      </Dialog>

      <Separator />

      {/* Limit Usage Indicator */}
      {limits && (
        <LimitUsageIndicator
          limitName={t('team:limitIndicator.teamMembers', 'Team Members')}
          current={limits.usage.users.current}
          max={limits.usage.users.limit}
          isUnlimited={limits.usage.users.unlimited}
          entityName={t('team:limitIndicator.members', 'members')}
        />
      )}

      {/* Main Tabs */}
      <Card className="overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="py-3">
            <TabsList>
              <TabsTrigger value="members">
                <Users className="w-4 h-4" />
                {t('team:tabs.members', 'Team Members')}
              </TabsTrigger>
              <TabsTrigger value="invitations">
                <Mail className="w-4 h-4" />
                {t('team:tabs.invitations', 'Invitations')}
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          {/* ============ TEAM MEMBERS TAB ============ */}
          <TabsContent value="members" className="space-y-4">
            {isLoadingMembers ? (
              <div className="flex justify-center py-12">
                <Spinner className="w-8 h-8" />
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="py-12 px-6">
                <div className="text-center space-y-2">
                  <Users className="w-12 h-12 mx-auto text-soft" />
                  <h3 className="text-h3 text-ink">{t('team:empty.noMembers', 'No team members')}</h3>
                  <p className="text-soft">
                    {t('team:empty.noMembersDescription', 'Invite your first team member to get started')}
                  </p>
                </div>
              </div>
            ) : (
              // ============ LIST VIEW ============
              <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line bg-surface-2">
                        <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                          {t('team:table.name', 'Name')}
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                          {t('team:table.role', 'Role')}
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden lg:table-cell">
                          {t('team:table.services', 'Services')}
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden sm:table-cell">
                          {t('team:table.status', 'Status')}
                        </th>
                        <th scope="col" className="px-4 py-3 text-right">
                          <span className="sr-only">{t('team:table.actions', 'Actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers.map((member) => (
                        <tr
                          key={member.id}
                          onClick={() => handleNavigateToDetail(member)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleNavigateToDetail(member);
                            }
                          }}
                          className={cn(
                            'border-b border-line last:border-0 cursor-pointer transition-colors',
                            'hover:bg-surface-2 focus:outline-none focus:bg-surface-2',
                            isSelf(member.id) && 'bg-brand-50/30'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div className="flex flex-col min-w-0">
                              <div className="flex flex-wrap items-center gap-2 min-w-0">
                                <span className="font-medium text-ink break-words">
                                  {member.firstName} {member.lastName}
                                </span>
                                {isSelf(member.id) && (
                                  <Badge tone="neutral" className="flex-shrink-0">
                                    {t('team:badges.you', 'You')}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-soft break-all">{member.email}</span>
                              <div className="mt-1 sm:hidden">
                                <Badge tone={member.active ? 'success' : 'neutral'}>
                                  {member.active ? t('common:status.active', 'Active') : t('common:status.inactive', 'Inactive')}
                                </Badge>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex flex-wrap items-center gap-1">
                              <Badge tone="neutral" className="gap-1">
                                {member.role === 'admin' ? <Shield className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
                                {t(`team:badges.${member.role}`, member.role)}
                              </Badge>
                              {member.provider && (
                                <Badge tone="info" className="gap-1">
                                  <Briefcase className="w-3 h-3" />
                                  {t('team:badges.provider', 'Provider')}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {member.provider && member.provider.services.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {member.provider.services.slice(0, 3).map((s) => (
                                  <Badge key={s.id} tone="neutral">
                                    {s.name}
                                  </Badge>
                                ))}
                                {member.provider.services.length > 3 && (
                                  <Badge tone="neutral">
                                    +{member.provider.services.length - 3}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-soft">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <Badge tone={member.active ? 'success' : 'neutral'}>
                              {member.active ? t('common:status.active', 'Active') : t('common:status.inactive', 'Inactive')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <MemberActionsMenu member={member} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* ============ INVITATIONS TAB ============ */}
            <TabsContent value="invitations" className="space-y-6 p-6">
              <Tabs value={invitationStatusFilter} onValueChange={setInvitationStatusFilter}>
                <TabsList>
                  <TabsTrigger value="">
                    {t('invitations:invitationTabs.all', 'All')}
                  </TabsTrigger>
                  <TabsTrigger value="pending">
                    {t('invitations:invitationTabs.pending', 'Pending')}
                  </TabsTrigger>
                  <TabsTrigger value="accepted">
                    {t('invitations:invitationTabs.accepted', 'Accepted')}
                  </TabsTrigger>
                  <TabsTrigger value="expired">
                    {t('invitations:invitationTabs.expired', 'Expired')}
                  </TabsTrigger>
                  <TabsTrigger value="revoked">
                    {t('invitations:invitationTabs.revoked', 'Revoked')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {isLoadingInvitations ? (
                <div className="flex justify-center py-12">
                  <Spinner className="w-8 h-8" />
                </div>
              ) : !invitations || invitations.length === 0 ? (
                <div className="py-12">
                  <div className="text-center space-y-2">
                    <Mail className="w-12 h-12 mx-auto text-soft" />
                    <h3 className="text-h3 text-ink">{t('invitations:empty.title', 'No invitations')}</h3>
                    <p className="text-soft">
                      {invitationStatusFilter
                        ? t('invitations:empty.withFilter', 'No {{status}} invitations found', { status: invitationStatusFilter })
                        : t('invitations:empty.noFilter', 'No invitations have been sent yet')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {invitations.map((invitation: UserInvitation) => (
                    <Card key={invitation.id} variant="inset">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base sm:text-lg truncate">
                            {invitation.firstName && invitation.lastName
                              ? `${invitation.firstName} ${invitation.lastName}`
                              : invitation.email}
                          </h3>
                          {invitation.firstName && invitation.lastName && (
                            <p className="text-sm text-soft truncate">{invitation.email}</p>
                          )}
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-11 w-11 p-0 flex-shrink-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {invitation.status === 'pending' && (
                              <>
                                <DropdownMenuItem onClick={() => handleResendInvitation(invitation.id)}>
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  {t('invitations:actions.resend', 'Resend')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRevokeInvitation(invitation.id)}>
                                  <X className="w-4 h-4 mr-2" />
                                  {t('invitations:actions.revoke', 'Revoke')}
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDeleteInvitation(invitation.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('invitations:actions.delete', 'Delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {getStatusBadge(invitation.status)}
                        <Badge tone="neutral">{invitation.role}</Badge>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-soft text-xs sm:text-sm">
                            {t('invitations:card.invitedBy', 'Invited by')}
                          </p>
                          <p className="font-medium break-words">{invitation.invitedBy.name}</p>
                        </div>
                        <div>
                          <p className="text-soft text-xs sm:text-sm">
                            {t('invitations:card.sent', 'Sent')}
                          </p>
                          <p className="font-medium">
                            {new Date(invitation.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        {invitation.status === 'pending' && (
                          <div>
                            <p className="text-soft text-xs sm:text-sm">
                              {t('invitations:card.expiresIn', 'Expires in')}
                            </p>
                            <p className="font-medium flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {getTimeUntilExpiry(invitation.expiresAt)}
                            </p>
                          </div>
                        )}
                        {invitation.status === 'accepted' && invitation.acceptedAt && (
                          <div>
                            <p className="text-soft text-xs sm:text-sm">
                              {t('invitations:card.accepted', 'Accepted')}
                            </p>
                            <p className="font-medium">
                              {new Date(invitation.acceptedAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-soft text-xs sm:text-sm">
                            {t('invitations:card.sentCount', 'Times sent')}
                          </p>
                          <p className="font-medium">{invitation.sendCount}x</p>
                        </div>
                      </div>

                      {invitation.personalMessage && (
                        <div className="bg-muted p-3 rounded-md">
                          <p className="text-sm italic break-words">{invitation.personalMessage}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>

      {/* ============ DIALOGS ============ */}

      <EditMemberDialog
        open={isEditMemberDialogOpen}
        onOpenChange={setIsEditMemberDialogOpen}
        member={selectedMember ? memberToUser(selectedMember) : null}
        provider={selectedMember ? memberToProvider(selectedMember) : null}
        services={services}
      />

      {/* Upgrade Dialog */}
      {upgradeError && (
        <UpgradeDialog
          open={!!upgradeError}
          onClose={() => setUpgradeError(null)}
          error={upgradeError}
        />
      )}

      {/* Upgrade Nudge Dialog */}
      <UpgradeNudge
        isOpen={showUpgradeNudge}
        onClose={() => setShowUpgradeNudge(false)}
        feature="users"
      />

      {/* Mobile FAB for inviting team member */}
      <FloatingActionButton
        icon={<Plus className="w-5 h-5" />}
        label={t('team:inviteButton', 'Invite Team Member')}
        onClick={() => {
          if (canPerformAction('users') && isFeatureEnabled('teamCollaboration')) {
            setIsInviteDialogOpen(true);
          } else {
            setShowUpgradeNudge(true);
          }
        }}
      />
    </div>
  );
}
